import { useState, useEffect } from 'react'
import styles from './CaseDetail.module.css'
import { API_BASE_URL, getImageUrl } from '../config'
import LockForm, { PDF_LOCK_MARKER } from './LockForm'

interface CaseOnlyDetailProps {
  caseId?: number
  onPdfUrlLoaded?: (url: string) => void
  isUnlocked?: boolean
  onUnlock?: () => void
}

type PdfImageItem = string | typeof PDF_LOCK_MARKER

const parsePdfImages = (raw: string | string[]): PdfImageItem[] => {
  if (Array.isArray(raw)) {
    return raw
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

const PDFViewer = ({
  images,
  isUnlocked,
  onUnlock,
  caseId,
  pdfDownloadUrl,
}: {
  images: PdfImageItem[]
  isUnlocked: boolean
  onUnlock: () => void
  caseId?: number
  pdfDownloadUrl?: string
}) => {
  if (!images || images.length === 0) {
    return <div className={styles.pdfError}>暂无案例内容</div>
  }

  const lockIndex = images.indexOf(PDF_LOCK_MARKER)

  // Never show images after __LOCK__, regardless of unlock status
  const visibleImages = lockIndex !== -1
    ? images.slice(0, lockIndex)
    : images

  if (visibleImages.length === 0 && lockIndex !== -1) {
    return (
      <div className={styles.pdfContainer}>
        <LockForm
          isUnlocked={isUnlocked}
          onUnlock={onUnlock}
          caseId={caseId}
          type="case"
          pdfDownloadUrl={pdfDownloadUrl}
        />
      </div>
    )
  }

  return (
    <div className={styles.pdfContainer}>
      {visibleImages.map((item, index) => (
        <div key={index} className={styles.pdfPage}>
          <img src={getImageUrl(item as string)} alt={`案例页面 ${index + 1}`} className={styles.pdfImage} />
        </div>
      ))}
      {lockIndex !== -1 && (
        <LockForm
          isUnlocked={isUnlocked}
          onUnlock={onUnlock}
          caseId={caseId}
          type="case"
          pdfDownloadUrl={pdfDownloadUrl}
        />
      )}
    </div>
  )
}

const CaseOnlyDetail = ({
  caseId,
  onPdfUrlLoaded,
  isUnlocked = false,
  onUnlock,
}: CaseOnlyDetailProps) => {
  const [loading, setLoading] = useState(false)
  const [pdfImages, setPdfImages] = useState<PdfImageItem[]>([])
  const [rawDownloadUrl, setRawDownloadUrl] = useState<string>('')

  useEffect(() => {
    if (!caseId) return

    const fetchCaseDetail = async () => {
      setLoading(true)
      try {
        const response = await fetch(`${API_BASE_URL}/api/cases/${caseId}`)
        const result = await response.json()
        if (result.success) {
          const data = result.data

          const parsedImages = parsePdfImages(data.pdf_images)
          setPdfImages(parsedImages)

          const rawUrl = data.pdf_download_url || data.pdf_url || ''
          setRawDownloadUrl(rawUrl)
          if (rawUrl) {
            onPdfUrlLoaded?.(rawUrl)
          }
        }
      } catch (error) {
        console.error('Failed to fetch case detail:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchCaseDetail()
  }, [caseId])

  return (
    <div className={styles.container}>
      {loading ? (
        <div className={styles.loading}>加载中...</div>
      ) : (
        <div className={styles.contentWrapper}>
          <PDFViewer
            images={pdfImages}
            isUnlocked={isUnlocked}
            onUnlock={onUnlock || (() => {})}
            caseId={caseId}
            pdfDownloadUrl={getImageUrl(rawDownloadUrl)}
          />
        </div>
      )}
    </div>
  )
}

export default CaseOnlyDetail
